import {ConsentCategoryMutationRequest, ConsentCategoryQuery, ConsentCategorySource, ConsentCategoryStatus, ConsentSignerRole} from "@source/api/models/consentModels";
import {HOME_ROUTES} from "@source/utils/utils";
import type {ISelectItem} from "@source/components/UI/inputs/selectInput/selectInput";

export const roleOptions: Array<{role: ConsentSignerRole; label: string; description: string}> = [
  {role: ConsentSignerRole.EMPLOYEE, label: "Employee", description: "Staff assigned to the document"},
  {role: ConsentSignerRole.GP_CLINICAL, label: "GP / Clinician", description: "A GP or clinician signs on site or through their Brenna login"},
  {role: ConsentSignerRole.RESIDENT, label: "Resident", description: "The resident gives consent on site"},
  {role: ConsentSignerRole.FAMILY_MEMBER, label: "Family members", description: "Selected family contacts"},
];
export const roleLabel = (role: ConsentSignerRole) => roleOptions.find((option) => option.role === role)?.label || role;
export const categoryPath = (id: string) => `${HOME_ROUTES.ConsentDocumentsCategories}/${id}`;
export const statusItems: ISelectItem[] = [
  {id: ConsentCategoryStatus.ACTIVE, value: ConsentCategoryStatus.ACTIVE, title: "Active"},
  {id: ConsentCategoryStatus.ARCHIVED, value: ConsentCategoryStatus.ARCHIVED, title: "Archived"},
];

export const createCategoryListQuery = (
  status: ConsentCategoryStatus,
  search: string,
): ConsentCategoryQuery => ({status, search: search.trim() || undefined, sort: "desc"});

export const normalizeCategoryOrder = (
  roles: ConsentSignerRole[],
  order: ConsentSignerRole[],
): ConsentSignerRole[] => [...order.filter((role) => roles.includes(role)), ...roles.filter((role) => !order.includes(role))];

export const notificationEmailFields = [
  {key: "withdrawalNotificationEmail", label: "Withdrawal request", description: "Sent when someone asks to withdraw a consent."},
  {key: "withdrawnNotificationEmail", label: "Consent withdrawn", description: "Sent when a consent is withdrawn."},
  {key: "completedNotificationEmail", label: "Consent completed", description: "Sent when a consent is completed."},
  {key: "declinedNotificationEmail", label: "Consent declined", description: "Sent when a consent is declined."},
  {key: "escalationNotificationEmail", label: "Escalation", description: "Sent after all reminders while the consent is still incomplete."},
] as const;

export type NotificationEmailField = (typeof notificationEmailFields)[number]["key"];

export interface CategoryFormValues {
  name: string;
  source: ConsentCategorySource;
  templateId: string;
  roles: ConsentSignerRole[];
  signingOrderRequired: boolean;
  order: ConsentSignerRole[];
  withdrawalNotificationEmail: string;
  withdrawnNotificationEmail: string;
  completedNotificationEmail: string;
  declinedNotificationEmail: string;
  escalationNotificationEmail: string;
}

export const isNotificationEmailValid = (value: string) => !value.trim() || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

export const createCategoryPayload = (values: CategoryFormValues): ConsentCategoryMutationRequest => ({
  name: values.name.trim(),
  source: values.source,
  ...(values.source === ConsentCategorySource.TEMPLATE ? {templateId: values.templateId} : {}),
  requiredSignerRoles: values.roles,
  signingOrderRequired: values.signingOrderRequired,
  signingOrder: values.signingOrderRequired ? normalizeCategoryOrder(values.roles, values.order) : [],
  withdrawalNotificationEmail: values.withdrawalNotificationEmail.trim(),
  withdrawnNotificationEmail: values.withdrawnNotificationEmail.trim(),
  completedNotificationEmail: values.completedNotificationEmail.trim(),
  declinedNotificationEmail: values.declinedNotificationEmail.trim(),
  escalationNotificationEmail: values.escalationNotificationEmail.trim(),
});
